import { useLeadDeliveryLocation } from "@/hooks/useDeliveryZones";
import { formatMxn } from "@/lib/format-mxn";
import { mapsLink } from "@/lib/maps-link";

const COVERAGE_LABEL = {
  covered: "Con cobertura",
  not_covered: "Fuera de cobertura",
  ambiguous: "En el límite — falta calle y número",
  not_found: "No encontrada",
} as const;

const COVERAGE_TONE = {
  covered: "text-emerald-700",
  not_covered: "text-red-600",
  ambiguous: "text-amber-700",
  not_found: "text-ink-500",
} as const;

/**
 * The last delivery address the agent verified for this lead — the customer's
 * own words, how the map read them, and the verdict it got at the time. Renders
 * nothing for a lead with none, which is every lead of a shop without zones.
 */
export function LeadDeliveryLocationCard({ leadId }: { leadId: string }): React.JSX.Element | null {
  const { data: location } = useLeadDeliveryLocation(leadId);
  if (!location) return null;

  const href = mapsLink({ lat: location.lat, lng: location.lng });
  const words = [location.queryText, location.references].filter(Boolean).join(" — ");

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
        Dirección de entrega
      </p>
      <div className="flex flex-col gap-1 rounded-[12px] border border-ink-100 bg-white p-2.5">
        {words && <p className="text-xs text-ink-900">{words}</p>}
        {location.formattedAddress && location.formattedAddress !== words && (
          <p className="text-[11px] text-ink-500">{location.formattedAddress}</p>
        )}
        <p className={`text-[11px] font-medium ${COVERAGE_TONE[location.coverage]}`}>
          {COVERAGE_LABEL[location.coverage]}
          {location.coverage === "covered" && location.zoneName
            ? ` · zona ${location.zoneName}${
                location.feeCents === null
                  ? ""
                  : location.feeCents === 0
                    ? " · envío gratis"
                    : ` · envío ${formatMxn(location.feeCents)}`
              }`
            : ""}
        </p>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-medium text-moss underline"
          >
            Abrir en Maps
          </a>
        )}
      </div>
    </div>
  );
}
